import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { PreferredDestinations } from "./PreferredDestinations";
import { PropertyTypeSelect } from "./PropertyTypeSelect";
import { BudgetRangeInput } from "./BudgetRangeInput";
import { AccessibilityNeeds } from "./AccessibilityNeeds";
import { DietaryRestrictions } from "./DietaryRestrictions";

interface TravelPreferences {
  id: string;
  user_id: string;
  preferred_destinations: string[];
  preferred_property_types: string[];
  budget_range: {
    min: number;
    max: number;
  };
  accessibility_needs: string[];
  dietary_restrictions: string[];
  created_at: string;
  updated_at: string;
}

export const TravelPreferencesTab = () => {
  const { toast } = useToast();

  const { data: preferences, isLoading } = useQuery({
    queryKey: ["travel-preferences"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("travel_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;

      // If no preferences exist, create default ones
      if (!data) {
        const defaultPreferences = {
          user_id: user.id,
          preferred_destinations: [],
          preferred_property_types: [],
          budget_range: { min: 0, max: 1000 },
          accessibility_needs: [],
          dietary_restrictions: []
        };

        const { data: newPrefs, error: insertError } = await supabase
          .from("travel_preferences")
          .insert(defaultPreferences)
          .select()
          .single();

        if (insertError) throw insertError;

        return {
          ...newPrefs,
          budget_range: typeof newPrefs.budget_range === 'string' 
            ? JSON.parse(newPrefs.budget_range)
            : newPrefs.budget_range
        } as TravelPreferences;
      }

      // Parse budget_range if it's a string
      return {
        ...data,
        budget_range: typeof data.budget_range === 'string' 
          ? JSON.parse(data.budget_range)
          : data.budget_range
      } as TravelPreferences;
    }
  });

  const updatePreferences = useMutation({
    mutationFn: async (updates: Partial<TravelPreferences>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("travel_preferences")
        .update(updates)
        .eq("user_id", user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Travel preferences updated successfully",
      });
    },
    onError: (error) => {
      console.error('Error updating preferences:', error);
      toast({
        title: "Error",
        description: "Failed to update preferences. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleDestinationsChange = (destinations: string[]) => {
    updatePreferences.mutate({ preferred_destinations: destinations });
  };

  const handlePropertyTypeChange = (type: string) => {
    updatePreferences.mutate({
      preferred_property_types: [type],
    });
  };

  const handleBudgetChange = (type: 'min' | 'max', value: number) => {
    const currentBudget = preferences?.budget_range || { min: 0, max: 1000 };
    updatePreferences.mutate({
      budget_range: {
        ...currentBudget,
        [type]: value
      }
    });
  };

  const handleAccessibilityChange = (needs: string[]) => {
    updatePreferences.mutate({ accessibility_needs: needs });
  };

  const handleDietaryChange = (restrictions: string[]) => {
    updatePreferences.mutate({ dietary_restrictions: restrictions });
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-secondary rounded w-1/4"></div>
          <div className="h-8 bg-secondary rounded"></div>
          <div className="h-8 bg-secondary rounded"></div>
          <div className="h-8 bg-secondary rounded"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Travel Preferences</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Customize your travel experience
        </p>
      </div>

      <div className="space-y-6">
        <PreferredDestinations
          destinations={preferences?.preferred_destinations || []}
          onChange={handleDestinationsChange}
        />

        <PropertyTypeSelect
          propertyType={preferences?.preferred_property_types?.[0] || ''}
          onChange={handlePropertyTypeChange}
        />

        <BudgetRangeInput
          budgetRange={{
            min: preferences?.budget_range?.min || 0,
            max: preferences?.budget_range?.max || 1000
          }}
          onBudgetChange={handleBudgetChange}
        />

        <AccessibilityNeeds
          selected={preferences?.accessibility_needs || []}
          onSelect={handleAccessibilityChange}
        />

        <DietaryRestrictions
          selected={preferences?.dietary_restrictions || []}
          onSelect={handleDietaryChange}
        />
      </div>
    </Card>
  );
};